// Pure helpers for the Today page. Kept out of the components so node --test
// can reach them without a JSX transform.
//
// Nothing here decides what is due or in what order: today_platform_v1
// (migration 0271) does. These only turn what it returns into words.

export type TodayView = 'platform' | 'mine'

export type PlatformItem = {
  kind: string
  item_key: string
  title: string
  detail: string | null
  since_on: string | null
  age_days: number | null
  item_count: number
  link: string
}

export type PlatformGroup = {
  kind: string
  label: string
  count: number
  items: PlatformItem[]
}

/** Anything the server sends that is not 'platform' opens My interviews. */
export function asTodayView(v: unknown): TodayView {
  return v === 'platform' ? 'platform' : 'mine'
}

export function platformTotal(groups: PlatformGroup[]): number {
  return groups.reduce((n, g) => n + (Number(g.count) || 0), 0)
}

// Spelled out rather than taken from toLocaleDateString: the browser's ICU data
// decides whether September is "Sep" or "Sept", so the same date read
// differently on two phones.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * "24 Sep" from a yyyy-mm-dd date, read as a calendar date and not a UTC
 * instant. The year is added when it is not this year: task #893 carries a
 * target date in 2001, and "since 16 Sep" beside 9139 days hides the typo that
 * the year exposes.
 */
export function shortDate(iso: string, thisYear: number = new Date().getFullYear()): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  const day = `${d} ${MONTHS[(m ?? 1) - 1] ?? ''}`.trim()
  return y === thisYear ? day : `${day} ${y}`
}

export function ageText(
  item: Pick<PlatformItem, 'since_on' | 'age_days'>,
  thisYear: number = new Date().getFullYear(),
): string | null {
  // No date is shown as no date. A service is due by distance, and printing
  // "0 days" for it would be a figure nobody measured (rule 6).
  if (!item.since_on || item.age_days === null || item.age_days === undefined) return null
  if (item.age_days === 0) return 'Today'
  if (item.age_days < 0) return `In ${-item.age_days} day${item.age_days === -1 ? '' : 's'}`
  return `${item.age_days} day${item.age_days === 1 ? '' : 's'}, since ${shortDate(item.since_on, thisYear)}`
}

export function countText(view: TodayView, count: number | null): string {
  if (count === null) return '…'
  return view === 'platform'
    ? `${count} item${count === 1 ? '' : 's'} to clear`
    : `${count} interview${count === 1 ? '' : 's'} awaiting a decision`
}

/**
 * Who may score an interview round, for the line shown to everybody else.
 * Interview 2 has two scorers (migration 0270); Interview 1 and reference
 * calls have one.
 */
export function scorerNames(
  stage: string,
  managerName: string | null | undefined,
  secondName: string | null | undefined,
): string[] {
  return [managerName, stage === 'interview_2' ? secondName : null].filter(
    (n): n is string => typeof n === 'string' && n.trim().length > 0,
  )
}
